import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatMoney } from '../api';
import { Badge, Card, CheckField, Empty, Loading, Notice } from '../components/ui';

interface ZahlbareRechnung {
  id: string;
  number: string;
  supplier_name: string | null;
  gross_cents: number;
  creditor_iban: string | null;
  creditor_bic: string | null;
  due_date: string | null;
}

export function PaymentsPage() {
  const [rechnungen, setRechnungen] = useState<ZahlbareRechnung[] | null>(null);
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError('');
    try {
      const r = await api<ZahlbareRechnung[]>('/api/incoming/zahlbar');
      setRechnungen(r);
      // Vorauswahl: alle mit IBAN.
      setWahl(new Set(r.filter((x) => x.creditor_iban).map((x) => x.id)));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  function umschalten(id: string) {
    setWahl((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  }

  async function erzeugen() {
    const ids = [...wahl];
    if (ids.length === 0) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await downloadFile('/api/incoming/sepa', { method: 'POST', body: { invoiceIds: ids } });
      setMessage('SEPA-Datei erzeugt. Die enthaltenen Rechnungen sind als angewiesen (bezahlt) markiert.');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const summe = (rechnungen ?? [])
    .filter((r) => wahl.has(r.id))
    .reduce((s, r) => s + r.gross_cents, 0);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card
        title="SEPA-Zahllauf"
        actions={
          <button className="primary" onClick={erzeugen} disabled={busy || wahl.size === 0}>
            SEPA-Datei erzeugen ({wahl.size})
          </button>
        }
      >
        <div className="small muted">
          Freigegebene Eingangsrechnungen mit Kreditor-IBAN. Die erzeugte pain.001-Datei wird ins
          Online-Banking eingespielt; die einbezogenen Rechnungen werden als angewiesen markiert und
          erscheinen danach nicht mehr hier. Summe der Auswahl:{' '}
          <strong>{formatMoney(summe)}</strong>.
        </div>
      </Card>

      <Card padded={false}>
        {!rechnungen ? (
          <Loading />
        ) : rechnungen.length === 0 ? (
          <Empty>Keine freigegebenen Rechnungen zur Zahlung.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th />
                  <th>Rechnung</th>
                  <th>Kreditor</th>
                  <th>IBAN</th>
                  <th>fällig</th>
                  <th className="num">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {rechnungen.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.creditor_iban ? (
                        <CheckField label="" checked={wahl.has(r.id)} onChange={() => umschalten(r.id)} />
                      ) : (
                        <Badge kind="warn">keine IBAN</Badge>
                      )}
                    </td>
                    <td className="mono">{r.number}</td>
                    <td className="small">{r.supplier_name ?? '—'}</td>
                    <td className="small mono">{r.creditor_iban ?? '—'}</td>
                    <td className="small muted">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="num">{formatMoney(r.gross_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
